/**
 * Game Mode Validation Utilities
 *
 * Provides functions for validating and sanitizing game mode input for QR code URLs.
 * Game mode values must be lowercase, contain no whitespace, and be at most 20 characters.
 *
 * Requirements: 5.3, 5.4 - Game mode input validation and sanitization
 */

/**
 * Maximum allowed length for game mode values
 */
export const MAX_GAME_MODE_LENGTH = 20;

/**
 * Result of game mode validation
 */
export interface GameModeValidationResult {
  /** Whether the original input was already valid (no sanitization needed) */
  readonly valid: boolean;
  /** The sanitized game mode value (lowercase, no whitespace, max 20 chars) */
  readonly sanitized: string;
}

/**
 * Validates and sanitizes game mode input.
 *
 * Sanitization rules:
 * - Converts to lowercase
 * - Removes all whitespace characters
 * - Truncates to maximum 20 characters
 *
 * The `valid` field indicates whether the original input was already valid
 * (i.e., no sanitization was needed). This is useful for providing feedback
 * to users about whether their input was modified.
 *
 * @param value - The game mode input string to validate
 * @returns Object containing validity status and sanitized value
 *
 * @example
 * validateGameMode('MyGame')     // { valid: false, sanitized: 'mygame' }
 * validateGameMode('my game')    // { valid: false, sanitized: 'mygame' }
 * validateGameMode('mygame')     // { valid: true, sanitized: 'mygame' }
 * validateGameMode('a'.repeat(25)) // { valid: false, sanitized: 'a'.repeat(20) }
 *
 * Requirements: 5.3 - Game mode input only accepts lowercase without whitespace
 * Requirements: 5.4 - Game mode input enforces maximum 20 characters
 */
export function validateGameMode(value: string): GameModeValidationResult {
  const sanitized = value.toLowerCase().replace(/\s/g, '').slice(0, MAX_GAME_MODE_LENGTH);

  return {
    valid: sanitized === value && value.length <= MAX_GAME_MODE_LENGTH,
    sanitized,
  };
}
